import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Icon } from 'shared/ui/Icon'
import { Typography } from 'shared/ui/Typography'
import { uiStore } from 'shared/store/uiStore'
import { LINK_AI_AGENT } from 'shared/constants/links'
import { type IAICallingType } from 'entities/AICalling/api/types'
import { ChatbotPath } from 'pages/chatbot/route/chatbotPath'
import { UnifiedMessageField } from 'pages/chatbot/ui/UnifiedMessageField/UnifiedMessageField'
import styles from './styles.module.scss'
import { CALLING_CHIPS, type IChipConfig, TEXTING_CHIPS } from './template'

type ICreateAgentModalProps = {
  type: string
  onClose: () => void
}

export const CreateAgentModal = observer(({ type, onClose }: ICreateAgentModalProps) => {
  const [value, setValue] = useState('')
  const [selectedChipId, setSelectedChipId] = useState<string | null>(null)
  const [hoveredChipId, setHoveredChipId] = useState<string | null>(null)

  const chips = type === 'calling' ? CALLING_CHIPS : TEXTING_CHIPS
  const hoveredChip = chips.find((chip) => chip.id === hoveredChipId)
  const placeholder = hoveredChip?.placeholder || 'Describe what your AI agent should do...'

  const handleChange = (value: string) => {
    setValue(value)
    if (!value) setSelectedChipId(null)
  }

  const handleChipClick = (chip: IChipConfig) => {
    setValue(chip.placeholder)
    setSelectedChipId(chip.id)
  }

  const handleSubmit = () => {
    onClose()

    if (type === 'calling') {
      const callingType = (selectedChipId ?? 'custom') as IAICallingType
      uiStore.changeRoute({
        path: ChatbotPath.callingNew(callingType),
        options: { state: { initialInstruction: value } },
      })
    } else {
      uiStore.changeRoute({
        path: ChatbotPath.newChat,
        options: { state: { initialInstruction: value } },
      })
    }
  }

  return (
    <>
      <div className={styles.headerBackground} />
      <div className={styles.container}>
        <div className={styles.headerWrapper}>
          <Icon icon='stars' fontSize={32} color='var(--purple-70)' className={styles.starsIcon} />
          <Typography variant='body-xl-medium' ariaLabel='CreateAgentModal'>
            {"What you'd like to create?"}
          </Typography>
          <Typography variant='body-lg-regular' ariaLabel='CreateAgentModal' color='tertiary'>
            Not sure what to create?{' '}
            <a href={LINK_AI_AGENT} className={styles.guideLink} target={'_blank'} rel='noreferrer'>
              Check the guide
            </a>
          </Typography>
        </div>
        <UnifiedMessageField
          placeholder={placeholder}
          value={value}
          hoveredChipId={hoveredChipId}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          disabledBtn={!value}
        />
        <div className={styles.chipsRow}>
          {chips.map((chip) => (
            <div
              key={chip.id}
              className={classNames(styles.chip, {
                [styles.chip_selected]: selectedChipId === chip.id,
              })}
              onMouseEnter={() => setHoveredChipId(chip.id)}
              onMouseLeave={() => setHoveredChipId(null)}
              onClick={() => handleChipClick(chip)}
            >
              <Icon
                icon={chip.icon}
                color={
                  selectedChipId === chip.id
                    ? 'var(--content-brand-secondary)'
                    : 'var(--content-primary-tertiary)'
                }
              />
              {chip.label}
            </div>
          ))}
        </div>
      </div>
    </>
  )
})
